var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EmptyState, ErrorBoundary, FlexBox } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { memo, useCallback, useMemo } from 'react';
import { useMount, useUpdateEffect } from 'react-use';
import { AnalyticsContext } from 'analytics/components/controls/components/context/analyticsContext';
import { AnalyticsPeriod } from 'analytics/components/controls/components/period/analyticsPeriod';
import { InstrumentPanel } from 'analytics/components/panels/components/panel/components/panel/instrumentPanel';
import { InstrumentRenderer } from 'analytics/components/panels/components/panel/components/renderer/instrumentRenderer';
import { rglConfig } from 'analytics/components/panels/utils/theming/theming';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi, } from 'apis/analytics';
import { analytics } from 'app/utils/segment';
import { usePersistInstrumentSettings } from '../../../../utils/usePersistInstrumentSettings';
import { InstrumentRendererError } from '../../../renderer/components/error/instrumentRendererError';
import styles from './instrumentComparate.module.scss';
export const InstrumentComparate = memo(function InstrumentComparate({ panel, side, comparate: { controls }, onUpdate, }) {
    const { dashboard, filters } = useAnalyticsState();
    const [readInstrumentsQuery, { instruments, isLoading, isFetching, isError },] = analyticsApi.useLazyReadComparisonInstrumentsQuery({
        selectFromResult: (_a) => {
            var _b;
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { instruments: (_b = data === null || data === void 0 ? void 0 : data.data.instruments) !== null && _b !== void 0 ? _b : [] }));
        },
    });
    const { instrumentSettings, setInstrumentSettings } = usePersistInstrumentSettings();
    /** Fetches the list of instruments for the panel. */
    const readInstruments = useCallback((arg) => readInstrumentsQuery({
        side,
        payload: {
            id: panel.id,
            context: controls.context,
            period: controls.period,
            filters,
            settings: Object.assign(Object.assign({}, controls.settings), instrumentSettings),
            meta: Object.assign({}, arg === null || arg === void 0 ? void 0 : arg.meta),
        },
    }).unwrap(), [
        controls,
        filters,
        instrumentSettings,
        panel.id,
        readInstrumentsQuery,
        side,
    ]);
    // Preloads the data on mount for the left side of the comparison.
    useMount(() => {
        if (side === 'left')
            readInstruments();
    });
    // Updates the instrument data when the comparate controls or the instrument settings change.
    useUpdateEffect(() => {
        readInstruments();
    }, [controls, instrumentSettings]);
    const handleChangeContext = useCallback((context) => {
        onUpdate(side, { controls: Object.assign(Object.assign({}, controls), { context }) });
        analytics.analyticsCompareContextUpdated({
            dashboard: dashboard.title,
            panel: panel.title,
            side,
        });
    }, [controls, dashboard.title, onUpdate, panel.title, side]);
    const handleChangePeriod = useCallback((period) => {
        onUpdate(side, { controls: Object.assign(Object.assign({}, controls), { period }) });
        analytics.analyticsComparePeriodUpdated({
            dashboard: dashboard.title,
            panel: panel.title,
            side,
            value: period.values.length === 1
                ? period.values[0].value
                : 'dateWithDefaultTime',
            timezone: controls.settings.timezone,
        });
    }, [controls, dashboard.title, onUpdate, panel.title, side]);
    const handleChangeSettings = useCallback((newSettings) => {
        onUpdate(side, {
            controls: Object.assign(Object.assign({}, controls), { settings: Object.assign(Object.assign({}, controls.settings), newSettings) }),
        });
        analytics.analyticsComparePeriodUpdated({
            dashboard: dashboard.title,
            panel: panel.title,
            side,
            timezone: newSettings.timezone,
            value: controls.period.values.length === 1
                ? controls.period.values[0].value
                : 'dateWithDefaultTime',
            withSchedule: !!newSettings.schedule_id,
        });
    }, [controls, dashboard.title, onUpdate, panel.title, side]);
    /**
     * The panel height is calculated using the same method as react-grid-layout.
     * The height of the panel header, which is not shown in comparisons, and the
     * gap between it and the content below is subtracted from the calculated
     * height, so that the panel is only as tall as the content
     */
    const panelHeight = useMemo(() => {
        const panelHeaderHeight = parseInt(sizes.spacer[500]);
        const panelGap = parseInt(sizes.spacer[400]);
        return (Math.round(rglConfig.rowHeight * panel.layout.h +
            Math.max(0, panel.layout.h - 1) * rglConfig.margin[1]) -
            panelHeaderHeight -
            panelGap);
    }, [panel.layout.h]);
    const isEmpty = instruments.length === 0 && !isLoading && !isFetching && !isError;
    return (_jsxs("div", Object.assign({ className: styles.root }, { children: [_jsxs(FlexBox, Object.assign({ gap: "200" }, { children: [dashboard.context && (_jsx(AnalyticsContext, { context: dashboard.context, control: controls.context, onChange: handleChangeContext })), _jsx(AnalyticsPeriod, { period: controls.period, settings: controls.settings, onChangePeriod: handleChangePeriod, onChangeSettings: handleChangeSettings })] })), _jsx(InstrumentPanel, Object.assign({ panel: panel, showHeader: false, height: panelHeight }, { children: isEmpty ? (_jsx(FlexBox, Object.assign({ flex: "1", justifyContent: "center", alignItems: "center" }, { children: _jsx(EmptyState, { title: "Nothing to compare", description: "Select something above to see the comparison.", icon: "exchange" }) }))) : (_jsx(ErrorBoundary, Object.assign({ fallback: _jsx(InstrumentRendererError, {}) }, { children: _jsx(InstrumentRenderer, { panel: panel, instruments: instruments, isFetching: isFetching, isError: isError, onReadInstruments: readInstruments, onSetInstrumentSettings: setInstrumentSettings }) }))) }))] })));
});
